import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, LineChart, Line,
  XAxis, YAxis, CartesianGrid, BarChart, Bar, Legend,
} from "recharts";
import { CLASS_COLOURS, KPI } from "./_shared";

export function OverviewTab({ summary, trifr, loading }) {
  if (loading || !summary) return <div className="h-40 rounded-lg bg-white border border-slate-200 shimmer" />;
  const donutData = summary.classification_breakdown || [];
  const catData = [...(summary.category_breakdown || [])].sort((a, b) => b.value - a.value);
  const buData = [...(summary.bu_breakdown || [])].sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-4">
      {/* KPI row */}
      <div className="grid grid-cols-6 gap-3">
        <KPI testId="hseq-kpi-trifr-200k" label="TRIFR (200,000 hrs)"   value={summary.trifr_200k} tone="brand" hint="Recordable ÷ hrs × 200k" />
        <KPI testId="hseq-kpi-trifr-1m"   label="TRIFR (1,000,000 hrs)" value={summary.trifr_1m}   tone="brand" hint="Industry benchmark base" />
        <KPI testId="hseq-kpi-ltifr"      label="LTIFR (1M base)"       value={summary.ltifr_1m}   tone={summary.ltifr_1m > 0 ? "warn" : "good"} />
        <KPI testId="hseq-kpi-incidents"  label="Total Incidents (FY)"  value={summary.total_incidents} />
        <KPI testId="hseq-kpi-injured"    label="Injured Persons"       value={summary.total_injured} tone={summary.total_injured > 0 ? "warn" : "good"} />
        <KPI testId="hseq-kpi-actions"    label="Open Actions"          value={summary.open_actions} tone={summary.open_actions > 0 ? "warn" : "good"} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-medium text-slate-800 mb-2">Injury Classification</div>
          {donutData.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-10">No incidents this FY</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                  {donutData.map((d) => <Cell key={d.name} fill={CLASS_COLOURS[d.name] || "#94A3B8"} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "10px" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 col-span-2">
          <div className="text-sm font-medium text-slate-800 mb-2">Total Recordable Injury Frequency Rate — Over Time</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trifr}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month_label" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ fontSize: "12px" }} />
              <Legend wrapperStyle={{ fontSize: "10px" }} />
              <Line type="monotone" dataKey="trifr_1m" name="TRIFR (1M)" stroke="#00334C" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="trifr_200k" name="TRIFR (200k)" stroke="#FCEA00" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-medium text-slate-800 mb-2">Incidents by Category</div>
          {catData.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-10">—</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={catData} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: "12px" }} />
                <Bar dataKey="value" fill="#00334C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-medium text-slate-800 mb-2">Incidents by Business Unit</div>
          {buData.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-10">—</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={buData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: "12px" }} />
                <Bar dataKey="value" fill="#FCEA00" stroke="#00334C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
